import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowDownIcon,
  ArrowsPointingInIcon,
  ArrowUpIcon,
  BookOpenIcon,
  CheckCircleIcon,
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  MinusIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import GripIcon from "../../../components/Icons/GripIcon";
import OverflowMenu from "../../../components/ui/OverflowMenu";
import { compositionUnits, unitMoveTarget } from "../../../utils/passages";
import { answersHidden, cardClass, focusRing, isApproved, isTestBlocked, linkButtonClass, Spinner } from "./ui";

const MAX_POINTS = 999;
// The drag handle; on phones its touch target is 44px, without moving it (the margins take back the padding;
// relative: it stays on top where its target reaches over the next part of the row).
const handleClass = `shrink-0 cursor-grab touch-none rounded-md p-1 text-slate-300 transition duration-150 hover:bg-slate-100 hover:text-slate-500 active:cursor-grabbing disabled:cursor-not-allowed max-sm:relative max-sm:-m-2 max-sm:p-3 ${focusRing}`;
const passageOf = (item) => item?.question?.passage?.uuid || null;
// Rows of a passage block have no handle: a line in its place joins them under the block's grip.
const blockRail = (
  <span className="-my-2 flex w-7 shrink-0 justify-center self-stretch" aria-hidden="true">
    <span className="w-0.5 bg-indigo-200" />
  </span>
);

/**
 * Integer points >= 1 as [−][n][+]. The typed value is committed on Enter, on blur or after a short
 * pause; Esc puts the saved value back; ArrowUp/ArrowDown step by one. On phones it is 44px tall, with 44px
 * steps (touch targets).
 */
export function PointsStepper({ value, onCommit, disabled, label, size = "sm" }) {
  const [t] = useTranslation();
  const [draft, setDraft] = useState(String(value));
  const timer = useRef(null);

  useEffect(() => setDraft(String(value)), [value]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const commit = (raw) => {
    clearTimeout(timer.current);
    const n = Number.parseInt(raw, 10);
    if (Number.isInteger(n) && n >= 1 && n <= MAX_POINTS) {
      setDraft(String(n));
      if (n !== value) onCommit(n);
    } else {
      setDraft(String(value));
    }
  };
  const height = size === "lg" ? "h-11 sm:h-10" : "h-11 sm:h-8";
  const stepClass = `${height} flex w-11 items-center justify-center text-slate-500 transition duration-150 enabled:hover:bg-slate-100 enabled:hover:text-slate-900 disabled:cursor-not-allowed disabled:text-slate-300 sm:w-7 ${focusRing}`;

  return (
    <div className={`inline-flex shrink-0 items-stretch overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-inset ring-slate-300 ${disabled ? "opacity-50" : ""}`}>
      <button type="button" onClick={() => commit(String(value - 1))} disabled={disabled || value <= 1} aria-label={t("test_bank.builder.points_dec")} className={stepClass}>
        <MinusIcon className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <input
        type="text"
        inputMode="numeric"
        value={draft}
        disabled={disabled}
        aria-label={label}
        onChange={(e) => {
          const next = e.target.value.replace(/\D/g, "").slice(0, 3);
          setDraft(next);
          clearTimeout(timer.current);
          timer.current = setTimeout(() => commit(next), 800);
        }}
        onBlur={() => commit(draft)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit(draft);
          } else if (e.key === "Escape") {
            e.preventDefault();
            clearTimeout(timer.current);
            setDraft(String(value));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            commit(String(value + 1));
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            commit(String(value - 1));
          }
        }}
        className={`${height} w-10 border-0 border-x border-slate-200 bg-white px-0 text-center text-sm font-semibold tabular-nums text-slate-900 focus:ring-2 focus:ring-inset focus:ring-blue-600 disabled:cursor-not-allowed`}
      />
      <button type="button" onClick={() => commit(String(value + 1))} disabled={disabled || value >= MAX_POINTS} aria-label={t("test_bank.builder.points_inc")} className={stepClass}>
        <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

export function SaveStatus({ state, onRetry }) {
  const [t] = useTranslation();
  return (
    <p role="status" className="flex min-h-[1.25rem] flex-wrap items-center gap-1.5 text-xs text-slate-500">
      {state === "saving" && (
        <>
          <Spinner className="h-3.5 w-3.5" />
          {t("test_bank.builder.saving")}
        </>
      )}
      {state === "saved" && (
        <>
          <CheckCircleIcon className="h-4 w-4 text-emerald-500" aria-hidden="true" />
          {t("test_bank.builder.saved")}
        </>
      )}
      {state === "error" && (
        <>
          <ExclamationTriangleIcon className="h-4 w-4 text-red-500" aria-hidden="true" />
          <span className="text-red-700">{t("test_bank.builder.save_failed")}</span>
          <button type="button" onClick={onRetry} className={`${linkButtonClass} py-0 text-xs max-sm:min-h-11`}>
            {t("test_bank.builder.refresh")}
          </button>
        </>
      )}
      {state === "idle" && t("test_bank.builder.autosave")}
    </p>
  );
}

// The moves of a row or a block in its "⋯" menu: up, down and, with `ends`, to the start and to the end.
const moveItems = (t, index, count, onMove, { ends = true, busy = false } = {}) => {
  const first = busy || index === 0;
  const last = busy || index === count - 1;
  return [
    { key: "up", label: t("test_bank.builder.move_up"), Icon: ArrowUpIcon, onClick: () => onMove(index - 1), disabled: first },
    { key: "down", label: t("test_bank.builder.move_down"), Icon: ArrowDownIcon, onClick: () => onMove(index + 1), disabled: last },
    ends && { key: "top", label: t("test_bank.builder.move_top"), Icon: ChevronDoubleUpIcon, onClick: () => onMove(0), disabled: first },
    ends && { key: "bottom", label: t("test_bank.builder.move_bottom"), Icon: ChevronDoubleDownIcon, onClick: () => onMove(count - 1), disabled: last },
  ];
};

// The "⋯" menu of a row (the shared OverflowMenu, drawn over the page: the list scrolls on its own on wide screens).
// `index` of `count`: the row's place among what it moves past (the test's rows and blocks, or the rows of its
// block, then without Move to top/bottom). Removing is its red item, after a line.
function RowMenu({ code, index, count, ends = true, onView, onMove, onRemove }) {
  const [t] = useTranslation();
  return (
    <OverflowMenu
      portal
      label={t("test_bank.builder.row_actions", { code })}
      items={[
        { key: "view", label: t("test_bank.builder.view"), Icon: EyeIcon, onClick: onView },
        ...moveItems(t, index, count, onMove, { ends }),
        { key: "remove", label: t("test_bank.builder.remove_from_test"), Icon: TrashIcon, danger: true, onClick: onRemove },
      ]}
    />
  );
}

// The "⋯" menu of a passage block. `busy`: some of the passage's rows are still being added; the block moves or
// goes once they are saved.
function BlockMenu({ name, index, count, busy, onView, onEdit, onMove, onRemove }) {
  const [t] = useTranslation();
  return (
    <OverflowMenu
      portal
      label={t("test_bank.builder.passage.block_actions_named", { name })}
      items={[
        { key: "view", label: t("test_bank.builder.passage.view"), Icon: EyeIcon, onClick: onView },
        { key: "edit", label: t("test_bank.builder.passage.edit"), Icon: PencilSquareIcon, onClick: onEdit },
        ...moveItems(t, index, count, onMove, { busy }),
        { key: "remove", label: t("test_bank.builder.passage.remove"), Icon: TrashIcon, danger: true, onClick: onRemove, disabled: busy },
      ]}
    />
  );
}

// One question of the test: number, title, approval state, points and menu. `handle` comes first (the
// drag handle of a row of its own); `nodeRef`, `style` and `dragging` come from its sortable. A question its
// exam takes into tests only once approved shows a lock instead: removed, it can't come back for now. A question
// whose answers the user may not read says so quietly under its title, after its code (never cut off itself).
// Phones give the title the row's width and put the approval mark, the points and "⋯" on a line under it.
function RowItem({ item, number, active, flash, handle, menu, nodeRef, style, dragging = false, onOpen, onSetPoints }) {
  const [t] = useTranslation();
  const rowRef = useRef(null);
  const question = item.question || {};
  const code = question.code || "";
  const blocked = isTestBlocked(question);
  const hidden = answersHidden(question);

  // A row that just arrived is brought into view (the list scrolls on its own on wide screens).
  useEffect(() => {
    if (flash) rowRef.current?.scrollIntoView({ block: "nearest" });
  }, [flash]);

  return (
    <li
      ref={(node) => {
        nodeRef?.(node);
        rowRef.current = node;
      }}
      style={style}
      className={`relative flex items-center gap-1.5 px-2 py-2 sm:gap-2 sm:px-3 ${
        dragging ? "z-10 rounded-lg bg-white shadow-lg ring-1 ring-slate-200" : flash ? "bg-emerald-50" : active ? "bg-blue-50/70" : "bg-white"
      } transition-colors duration-700`}
    >
      {active && !dragging && <span className="absolute inset-y-0 left-0 w-0.5 bg-blue-600" aria-hidden="true" />}
      {handle}
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 gap-y-1 sm:flex-nowrap sm:gap-2">
        <span className="w-6 shrink-0 text-center text-xs font-semibold tabular-nums text-slate-400">{number}</span>
        <button type="button" data-row-title onClick={() => onOpen(item)} className={`min-w-0 flex-1 rounded-md text-left ${focusRing}`}>
          <span className="block truncate text-sm font-medium text-slate-900">{question.name || code || t("test_bank.builder.no_name")}</span>
          {/* The note goes under the code where the column has no room for both (the code is not cut for it). */}
          <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-slate-500">
            <span className="min-w-0 max-w-full truncate">{[code, question.subject?.name].filter(Boolean).join(" · ")}</span>
            {hidden && (
              <span className="inline-flex shrink-0 items-center gap-1 text-slate-400" data-testid="row-answers-hidden">
                <EyeSlashIcon className="h-3.5 w-3.5" aria-hidden="true" />
                {t("test_bank.builder.answers_hidden_row")}
              </span>
            )}
          </span>
        </button>
        <div className="flex items-center gap-1.5 max-sm:w-full max-sm:pl-[1.875rem] sm:gap-2">
          {item.pending ? (
            <span role="status" aria-label={t("test_bank.builder.saving")}>
              <Spinner />
            </span>
          ) : blocked ? (
            <span className="shrink-0 text-amber-600" title={t("test_bank.builder.blocked_row")}>
              <LockClosedIcon className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">{t("test_bank.builder.blocked_row")}</span>
            </span>
          ) : (
            !isApproved(question) &&
            question.status && (
              <span className="shrink-0 text-amber-500" title={t("test_bank.builder.not_approved")}>
                <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">{t("test_bank.builder.not_approved")}</span>
              </span>
            )
          )}
          <PointsStepper
            value={Number(item.points) || 1}
            onCommit={(n) => onSetPoints(item.uuid, n)}
            disabled={!!item.pending}
            label={t("test_bank.builder.points_for", { code })}
          />
          {!item.pending && <div className="max-sm:ml-auto">{menu}</div>}
        </div>
      </div>
    </li>
  );
}

// A question that is not in a passage block: dragged by its handle.
function SortableRow({ item, ...props }) {
  const [t] = useTranslation();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: item.uuid,
    disabled: !!item.pending,
  });
  return (
    <RowItem
      {...props}
      item={item}
      nodeRef={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      dragging={isDragging}
      handle={
        <button
          ref={setActivatorNodeRef}
          type="button"
          {...attributes}
          {...listeners}
          disabled={!!item.pending}
          aria-label={t("test_bank.builder.reorder_handle", { code: item.question?.code || "" })}
          className={handleClass}
        >
          <GripIcon className="h-5 w-5" />
        </button>
      }
    />
  );
}

/**
 * The rows of a passage as one unit of the list: dragged by the grip in its header, moved and removed
 * together (not while some of the passage's rows are being added, `busy`). Its rows keep the test's
 * numbers and move only inside it. A passage whose rows are apart shows a block per run, each `split`.
 */
function SortableBlock({ unit, index, count, busy, activeUuid, flashIds, onOpen, onSetPoints, onRemoveRow, onMoveRow, onMove, onGather, onView, onEdit, onRemove }) {
  const [t] = useTranslation();
  const labelId = useId();
  const { passage, items } = unit;
  const name = passage.name || "";
  const points = items.reduce((sum, it) => sum + (Number(it.points) || 0), 0);
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: unit.key, disabled: busy });

  return (
    <li
      ref={setNodeRef}
      data-passage={passage.uuid}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`relative ${isDragging ? "z-10 overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-slate-200" : ""}`}
    >
      <div className="flex items-center gap-1.5 bg-indigo-50/70 px-2 py-2 sm:gap-2 sm:px-3">
        <button
          ref={setActivatorNodeRef}
          type="button"
          {...attributes}
          {...listeners}
          disabled={busy}
          aria-label={t("test_bank.builder.passage.block_handle", { name })}
          className={handleClass}
        >
          <GripIcon className="h-5 w-5" />
        </button>
        <span className="flex w-6 shrink-0 justify-center">
          <BookOpenIcon className="h-4 w-4 text-indigo-600" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p id={labelId} className="truncate text-sm font-semibold text-indigo-950" title={name}>
            {t("test_bank.builder.passage.block_label", { name })}
          </p>
          <p className="truncate text-xs tabular-nums text-indigo-700">{t("test_bank.builder.passage.block_meta", { count: items.length, points })}</p>
        </div>
        <BlockMenu name={name} index={index} count={count} busy={busy} onView={onView} onEdit={onEdit} onMove={onMove} onRemove={onRemove} />
      </div>
      {/* Rows being added are apart from their block only until they are saved. */}
      {unit.split && !busy && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
          <span className="min-w-0 flex-1">{t("test_bank.builder.passage.split_warning")}</span>
          <button type="button" onClick={onGather} className={`${linkButtonClass} py-0.5 text-xs max-sm:min-h-11`}>
            <ArrowsPointingInIcon className="h-4 w-4" aria-hidden="true" />
            {t("test_bank.builder.passage.gather")}
          </button>
        </div>
      )}
      <ol aria-labelledby={labelId} className="divide-y divide-slate-100 border-t border-indigo-100">
        {items.map((item, i) => {
          const at = unit.start + i;
          return (
            <RowItem
              key={item.uuid}
              item={item}
              number={at + 1}
              active={activeUuid === item.question?.uuid}
              flash={flashIds.has(item.question?.uuid)}
              handle={blockRail}
              onOpen={onOpen}
              onSetPoints={onSetPoints}
              menu={
                <RowMenu
                  code={item.question?.code || ""}
                  index={i}
                  count={items.length}
                  ends={false}
                  onView={() => onOpen(item)}
                  onMove={(to) => onMoveRow(item.uuid, unit.start + to)}
                  onRemove={() => onRemoveRow(item, at)}
                />
              }
            />
          );
        })}
      </ol>
    </li>
  );
}

function EmptyComposition() {
  const [t] = useTranslation();
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
        <ClipboardDocumentListIcon className="h-6 w-6" aria-hidden="true" />
      </span>
      <p className="text-sm font-semibold text-slate-900">{t("test_bank.builder.empty_title")}</p>
      <ol className="space-y-2 text-left text-sm text-slate-600">
        {[1, 2, 3].map((n) => (
          <li key={n} className="flex items-start gap-2">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold text-blue-700">{n}</span>
            {t(`test_bank.builder.guide_${n}`)}
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * The test side of the builder: totals, points for new questions, and the ordered questions
 * (drag with the mouse or the keyboard, or use the row menu; points per question; remove).
 * The rows of a passage form a block that moves as one (move_passage through onMovePassage); a row of
 * its own moves past a whole block. Every row keeps its number in the test.
 * A question its exam takes into tests only once approved is marked: onRemove / onRemovePassage ask about
 * it first (a dialog of the builder), as it can't be added back for now. Questions whose answers the user may not
 * read in the bank (answers_hidden) are counted in one quiet line and named so on their rows; they are kept, moved
 * and scored as any other.
 */
export default function TestComposition({
  items,
  totals,
  saveState,
  onRetry,
  defaultPoints,
  onDefaultPointsChange,
  activeUuid,
  flashIds,
  onOpen,
  onSetPoints,
  onRemove,
  onMove,
  onMovePassage,
  onRemovePassage,
  onViewPassage,
  onEditPassage,
  onAddPassage,
}) {
  const [t] = useTranslation();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  // Rows of their own and passage blocks; without passages a unit is a row (its key is the row's uuid).
  const units = useMemo(() => compositionUnits(items), [items]);
  const ids = useMemo(() => units.map((u) => u.key), [units]);
  const busyPassages = useMemo(() => new Set(items.filter((it) => it.pending).map(passageOf).filter(Boolean)), [items]);
  const listRef = useRef(null);
  const headingRef = useRef(null);
  // Removed rows take their menu button (and the keyboard focus) with them: focus the row now in their place.
  const focusRowAt = (index) => {
    requestAnimationFrame(() => {
      const titles = listRef.current?.querySelectorAll("[data-row-title]") || [];
      const next = titles[Math.min(index, titles.length - 1)];
      (next || headingRef.current)?.focus();
    });
  };
  // onRemove / onRemovePassage resolve true once the rows are going, false if they stay: rows whose questions
  // could not come back (isTestBlocked) go only once the builder's dialog has asked, and it has closed.
  const removeAndRefocus = (item, index) => {
    // Gone, the row now in its place gets the focus; kept, the row itself.
    Promise.resolve(onRemove(item)).then(() => focusRowAt(index));
  };
  const removePassageAndRefocus = (unit) => {
    // Every row of the passage goes, also those of an earlier run: the next row moves up by all of them.
    const at = unit.start - items.slice(0, unit.start).filter((it) => passageOf(it) === unit.passage.uuid).length;
    Promise.resolve(onRemovePassage(unit.passage)).then((removed) => focusRowAt(removed ? at : unit.start));
  };
  // A unit dragged or moved by its menu from position `from` to `to` among the units.
  const moveUnit = (from, to) => {
    const target = unitMoveTarget(units, from, to);
    if (target?.type === "row") onMove(target.itemUuid, target.toIndex);
    else if (target?.type === "passage") onMovePassage(target.passageUuid, target.k);
  };
  // The rows of a split passage come together at this run (after the other rows before it). The note
  // with the button goes away: the first question of the block gets the focus.
  const gather = (unit) => {
    const uuid = unit.passage.uuid;
    onMovePassage(uuid, items.slice(0, unit.start).filter((it) => passageOf(it) !== uuid).length);
    requestAnimationFrame(() => listRef.current?.querySelector(`[data-passage="${uuid}"] [data-row-title]`)?.focus());
  };
  const notApproved = useMemo(() => items.filter((it) => it.question?.status && !isApproved(it.question)).length, [items]);
  // Of those, the ones that could not be added back once removed.
  const blocked = useMemo(() => items.filter((it) => !it.pending && isTestBlocked(it.question)).length, [items]);
  // Questions whose right answers the user is not shown: said once, quietly (nothing to fix).
  const hiddenAnswers = useMemo(() => items.filter((it) => answersHidden(it.question)).length, [items]);
  // Spoken positions are the numbers of the rows: a block's is that of its first row.
  const unitOf = (id) => units[ids.indexOf(id)];
  const landingOf = (activeId, overId) => {
    const target = unitMoveTarget(units, ids.indexOf(activeId), ids.indexOf(overId));
    if (!target) return (unitOf(activeId)?.start ?? 0) + 1;
    return (target.type === "row" ? target.toIndex : target.k) + 1;
  };
  const announce = (id, event, values) => {
    const unit = unitOf(id);
    return unit?.kind === "passage"
      ? t(`test_bank.builder.passage.dnd_${event}`, { name: unit.passage.name || "", ...values })
      : t(`test_bank.builder.dnd.${event}`, { code: unit?.item.question?.code || "", ...values });
  };
  const announcements = {
    onDragStart: ({ active }) => announce(active.id, "picked", { position: (unitOf(active.id)?.start ?? 0) + 1, total: items.length }),
    onDragOver: ({ active, over }) => (over ? announce(active.id, "moved", { position: landingOf(active.id, over.id) }) : undefined),
    onDragEnd: ({ active, over }) => (over ? announce(active.id, "dropped", { position: landingOf(active.id, over.id) }) : undefined),
    onDragCancel: ({ active }) => announce(active.id, "cancelled"),
  };

  return (
    <section aria-labelledby="builder-test-title" className={`${cardClass} flex min-h-0 flex-col xl:h-full`}>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 border-b border-slate-100 px-4 py-3">
        <h2 ref={headingRef} id="builder-test-title" tabIndex={-1} className="text-base font-semibold text-slate-900 outline-none">
          {t("test_bank.builder.tab_test")}
        </h2>
        <SaveStatus state={saveState} onRetry={onRetry} />
      </div>
      <dl className="grid grid-cols-2 divide-x divide-slate-100 border-b border-slate-100">
        <div className="px-4 py-3">
          <dt className="text-xs font-medium text-slate-500">{t("test_bank.builder.stat_questions")}</dt>
          <dd className="mt-0.5 text-xl font-semibold tabular-nums text-slate-900">{totals.count}</dd>
        </div>
        <div className="px-4 py-3">
          <dt className="text-xs font-medium text-slate-500">{t("test_bank.editor.stat_points")}</dt>
          <dd className="mt-0.5 text-xl font-semibold tabular-nums text-slate-900">{totals.points}</dd>
        </div>
      </dl>
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-2.5 text-sm text-slate-600">
        <span>{t("test_bank.editor.default_points")}</span>
        <PointsStepper value={defaultPoints} onCommit={onDefaultPointsChange} label={t("test_bank.editor.default_points")} />
      </div>
      {(notApproved > 0 || blocked > 0) && (
        <div className="flex items-start gap-2 border-b border-amber-100 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
          <div className="min-w-0 space-y-1">
            <p>{t("test_bank.builder.not_approved_count", { count: notApproved })}</p>
            {blocked > 0 && (
              <p className="flex items-start gap-1.5">
                <LockClosedIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                {t("test_bank.builder.blocked_count", { count: blocked })}
              </p>
            )}
          </div>
        </div>
      )}
      {hiddenAnswers > 0 && (
        <p className="flex items-start gap-2 border-b border-slate-100 px-4 py-2 text-sm text-slate-500" data-testid="answers-hidden-count">
          <EyeSlashIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
          <span className="min-w-0">{t("test_bank.builder.answers_hidden_count", { count: hiddenAnswers })}</span>
        </p>
      )}
      {items.length === 0 ? (
        <EmptyComposition />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          onDragEnd={({ active, over }) => {
            if (over && active.id !== over.id) moveUnit(ids.indexOf(active.id), ids.indexOf(over.id));
          }}
          accessibility={{ announcements, screenReaderInstructions: { draggable: t("test_bank.builder.dnd.instructions") } }}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <ol ref={listRef} className="min-h-0 flex-1 divide-y divide-slate-100 xl:overflow-y-auto">
              {units.map((unit, u) =>
                unit.kind === "passage" ? (
                  <SortableBlock
                    key={unit.key}
                    unit={unit}
                    index={u}
                    count={units.length}
                    busy={busyPassages.has(unit.passage.uuid)}
                    activeUuid={activeUuid}
                    flashIds={flashIds}
                    onOpen={onOpen}
                    onSetPoints={onSetPoints}
                    onRemoveRow={removeAndRefocus}
                    onMoveRow={onMove}
                    onMove={(to) => moveUnit(u, to)}
                    onGather={() => gather(unit)}
                    onView={() => onViewPassage(unit.passage)}
                    onEdit={() => onEditPassage(unit.passage)}
                    onRemove={() => removePassageAndRefocus(unit)}
                  />
                ) : (
                  <SortableRow
                    key={unit.key}
                    item={unit.item}
                    number={unit.start + 1}
                    active={activeUuid === unit.item.question?.uuid}
                    flash={flashIds.has(unit.item.question?.uuid)}
                    onOpen={onOpen}
                    onSetPoints={onSetPoints}
                    menu={
                      <RowMenu
                        code={unit.item.question?.code || ""}
                        index={u}
                        count={units.length}
                        onView={() => onOpen(unit.item)}
                        onMove={(to) => moveUnit(u, to)}
                        onRemove={() => removeAndRefocus(unit.item, unit.start)}
                      />
                    }
                  />
                )
              )}
            </ol>
          </SortableContext>
        </DndContext>
      )}
      {/* A side "add" action: a text link under the list. */}
      {onAddPassage && (
        <div className="border-t border-slate-100 px-2.5 py-1.5 sm:px-3">
          <button type="button" onClick={onAddPassage} className={`${linkButtonClass} max-sm:min-h-11`}>
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            {t("passages.context.button")}
          </button>
        </div>
      )}
    </section>
  );
}
